import { useMemo } from "react";
import { BoMeTenant, useGetBoMeQuery } from "../store/services/api";

export interface TenantOption {
  label: string;
  value: string;
}

export interface TenantOptions {
  /** True mientras se resuelve la identidad (/bo/me). */
  loading: boolean;
  /** Tenants que el admin puede ver, GRUPS primero. */
  options: TenantOption[];
  /**
   * True cuando el admin puede ver el agregado de todos los tenants (opción "Todos").
   * Único punto donde se decide: cuando exista el admin con acceso a varios tenants
   * pero no a todos, se cambia acá y nada más.
   */
  canSeeAllTenants: boolean;
  /** True cuando hay más de una alternativa: si no, no tiene sentido mostrar un selector. */
  canChooseTenant: boolean;
}

/**
 * Opciones de tenant del BO, compartidas por el selector del topbar y por los filtros
 * de las pantallas (métricas). Tener una sola fuente evita que las listas difieran.
 *
 * El valor seleccionado NO vive acá: el topbar lo guarda en sessionStorage (contexto
 * ambiente, ver api.ts) y las pantallas de reporte lo manejan como filtro propio.
 */
export function useTenantOptions(): TenantOptions {
  const { data: me, isLoading } = useGetBoMeQuery();

  const options = useMemo<TenantOption[]>(() => {
    if (!me) {
      return [];
    }

    const label = (tenant: BoMeTenant) =>
      tenant.id === me.defaultTenantId ? "GRUPS (B2C)" : tenant.provider || tenant.id;

    return [...me.tenants]
      .sort((a, b) => {
        if (a.id === me.defaultTenantId) return -1;
        if (b.id === me.defaultTenantId) return 1;
        return (a.provider || a.id).localeCompare(b.provider || b.id);
      })
      .map((tenant) => ({ label: label(tenant), value: tenant.id }));
  }, [me]);

  const canSeeAllTenants = me?.role === "SUPER_ADMIN";

  return {
    loading: isLoading,
    options,
    canSeeAllTenants,
    canChooseTenant: canSeeAllTenants ? options.length > 0 : options.length > 1,
  };
}
